import {
  ACCESS_GRANT_TTL_MS,
  DEFAULT_MEETING_SETTINGS,
  MEETING_STORAGE_KEYS,
} from '../constants/meetings.constants'
import { matchesMeetingId } from '../utils/recurrence'
import type {
  MeetingCredential,
  MeetingRecord,
} from '../models/meeting.model'

/**
 * Pure persistence layer for the meetings feature. Reads and writes
 * localStorage / sessionStorage and nothing else — no validation, no
 * derived status, no toasts.
 *
 * When the backend lands, this entire file is the one being replaced.
 * Every consumer should go through `meetings.service` (not here directly)
 * so that the swap is a one-file change.
 */

// ── helpers ─────────────────────────────────────────────────────────────

const readArray = <T>(key: string): T[] => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed as T[]
    // Legacy: previously the store used zustand/middleware/persist which
    // wraps the value as `{ state: { meetings: [...] }, version: N }`.
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.state &&
      Array.isArray(parsed.state.meetings)
    ) {
      return parsed.state.meetings as T[]
    }
    return []
  } catch {
    return []
  }
}

const writeArray = <T>(key: string, value: T[]) => {
  localStorage.setItem(key, JSON.stringify(value))
}

const readMap = <V>(key: string): Record<string, V> => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, V>)
      : {}
  } catch {
    return {}
  }
}

const writeMap = <V>(key: string, value: Record<string, V>) => {
  localStorage.setItem(key, JSON.stringify(value))
}

// ── meeting credentials (password hash + salt) ──────────────────────────

export const meetingCredentialsRepo = {
  all(): Record<string, MeetingCredential> {
    return readMap<MeetingCredential>(MEETING_STORAGE_KEYS.CREDENTIALS)
  },
  get(meetingId: string): MeetingCredential | undefined {
    return this.all()[meetingId]
  },
  put(credential: MeetingCredential): void {
    const map = this.all()
    map[credential.meetingId] = credential
    writeMap(MEETING_STORAGE_KEYS.CREDENTIALS, map)
  },
  remove(meetingId: string): void {
    const map = this.all()
    if (!(meetingId in map)) return
    delete map[meetingId]
    writeMap(MEETING_STORAGE_KEYS.CREDENTIALS, map)
  },
}

// ── meeting access grants (sessionStorage, per-tab) ─────────────────────

interface AccessGrant {
  meetingId: string
  grantedAt: number
  expiresAt: number
}

const accessKey = (meetingId: string) =>
  `${MEETING_STORAGE_KEYS.ACCESS_GRANTS}:${meetingId}`

export const meetingAccessRepo = {
  grant(meetingId: string, ttlMs: number = ACCESS_GRANT_TTL_MS): void {
    const grant: AccessGrant = {
      meetingId,
      grantedAt: Date.now(),
      expiresAt: Date.now() + ttlMs,
    }
    sessionStorage.setItem(accessKey(meetingId), JSON.stringify(grant))
  },
  hasGrant(meetingId: string): boolean {
    try {
      const raw = sessionStorage.getItem(accessKey(meetingId))
      if (!raw) return false
      const grant = JSON.parse(raw) as AccessGrant
      if (grant.expiresAt <= Date.now()) {
        sessionStorage.removeItem(accessKey(meetingId))
        return false
      }
      return true
    } catch {
      return false
    }
  },
  revoke(meetingId: string): void {
    sessionStorage.removeItem(accessKey(meetingId))
  },
}

// ── meeting records ─────────────────────────────────────────────────────

/**
 * Backfills defaults for records persisted by the legacy store (no
 * `settings`, `participants`, `invitees`, etc.). Idempotent: a fully-formed
 * record passes through unchanged.
 */
const normalizeRecord = (raw: Partial<MeetingRecord>): MeetingRecord => {
  const id = (raw.id ?? raw.meetingId ?? '') as string
  const durationMinutes =
    (raw.durationMinutes as number | undefined) ??
    (raw.duration as number | undefined) ??
    0
  const now = Date.now()
  return {
    id,
    meetingId: (raw.meetingId as string | undefined) ?? id,
    title: (raw.title as string | undefined) ?? 'Meeting',
    description: raw.description as string | undefined,
    host: (raw.host as string | undefined) ?? 'Host',
    hostEmail: raw.hostEmail as string | undefined,
    hostId: raw.hostId as string | undefined,
    type: (raw.type as MeetingRecord['type'] | undefined) ?? 'instant',
    startTimeMs: (raw.startTimeMs as number | undefined) ?? now,
    endTimeMs: (raw.endTimeMs as number | undefined) ?? now,
    durationMinutes,
    actualDurationMinutes: raw.actualDurationMinutes as number | undefined,
    actualEndTimeMs: raw.actualEndTimeMs as number | undefined,
    inProgress: raw.inProgress as boolean | undefined,
    timezone:
      (raw.timezone as string | undefined) ??
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    status: (raw.status as MeetingRecord['status'] | undefined) ?? 'upcoming',
    start: (raw.start as string | undefined) ?? '',
    end: (raw.end as string | undefined) ?? '',
    duration: durationMinutes,
    attendees: Array.isArray(raw.attendees) ? raw.attendees : [],
    colors: Array.isArray(raw.colors) ? raw.colors : [],
    seats: typeof raw.seats === 'number' ? raw.seats : 2,
    // Carried through rather than defaulted: a local record has no permission
    // of its own to state, and inventing `false` here would hide the edit
    // controls from a host whose list came from storage.
    canModerate: raw.canModerate as boolean | undefined,
    invitees: Array.isArray(raw.invitees) ? raw.invitees : [],
    participants: Array.isArray(raw.participants) ? raw.participants : [],
    settings: { ...DEFAULT_MEETING_SETTINGS, ...(raw.settings ?? {}) },
    recurring: !!raw.recurring,
    recurringConfig: raw.recurringConfig,
    parentMeetingId: raw.parentMeetingId as string | undefined,
    series: raw.series,
    attachments: Array.isArray(raw.attachments) ? raw.attachments : [],
    createdAt: (raw.createdAt as number | undefined) ?? now,
    updatedAt: (raw.updatedAt as number | undefined) ?? now,
  }
}

/**
 * Only a record that says when it starts is a meeting this browser can list.
 *
 * `normalizeRecord` used to give one without a start time `Date.now()`, so it
 * landed in TODAY's list on every single read, with whatever else it lacked
 * filled in empty — `attendees: []` among them. The guest-join seed was exactly
 * that shape, and `MeetingList`'s avatar read `attendees[0].split(" ")`: the
 * dashboard crashed on every load after a guest join in that browser
 * (BUG-000533). Dropped here instead, and since every write goes through `all()`
 * first, the next write removes it from storage too.
 */
const hasStartTime = (raw: Partial<MeetingRecord>): boolean =>
  typeof raw.startTimeMs === 'number' && Number.isFinite(raw.startTimeMs)

export const meetingsRepo = {
  all(): MeetingRecord[] {
    return readArray<Partial<MeetingRecord>>(MEETING_STORAGE_KEYS.RECORDS)
      .filter(hasStartTime)
      .map(normalizeRecord)
  },

  findById(id: string): MeetingRecord | undefined {
    return this.all().find((m) => matchesMeetingId(m, id))
  },

  insert(record: MeetingRecord): MeetingRecord {
    const all = this.all()
    if (all.some((m) => m.id === record.id)) {
      throw new Error(`Meeting with id ${record.id} already exists`)
    }
    writeArray(MEETING_STORAGE_KEYS.RECORDS, [record, ...all])
    return record
  },

  update(id: string, patch: Partial<MeetingRecord>): MeetingRecord | null {
    const all = this.all()
    const idx = all.findIndex((m) => matchesMeetingId(m, id))
    if (idx === -1) return null
    const updated: MeetingRecord = {
      ...all[idx],
      ...patch,
      updatedAt: Date.now(),
    }
    all[idx] = updated
    writeArray(MEETING_STORAGE_KEYS.RECORDS, all)
    return updated
  },

  remove(id: string): boolean {
    const all = this.all()
    const next = all.filter((m) => m.id !== id && m.meetingId !== id)
    if (next.length === all.length) return false
    writeArray(MEETING_STORAGE_KEYS.RECORDS, next)
    // Cascade clean-ups.
    meetingCredentialsRepo.remove(id)
    meetingAccessRepo.revoke(id)
    return true
  },

  bulkReplace(records: MeetingRecord[]): void {
    writeArray(MEETING_STORAGE_KEYS.RECORDS, records)
  },

  /** Forget every stored meeting — on sign-out, so none reaches the next account. */
  clear(): void {
    try {
      localStorage.removeItem(MEETING_STORAGE_KEYS.RECORDS)
    } catch {
      // Storage disabled: there was nothing stored to leak.
    }
  },
}
